function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from './Button';
import FormControlWithAddon from './FormControlWithAddon';
import formStyles from './FormControls.module.scss';
const HiddenFileInput = React.forwardRef((props, ref) => React.createElement("input", _extends({
  ref: ref
}, props, {
  type: "file",
  value: // XXX: Need to specify a value of '' to get onChange to fire again when
  //  selecting the same file. This component isn't really controlled in
  //  any event.
  props.onChange ? '' : undefined,
  className: "sr-only"
})));
HiddenFileInput.displayName = 'HiddenFileInput'; // Not available in SSR.

function isFile(value) {
  return global.File && value instanceof global.File;
}

const FormControlFileInput = React.forwardRef(({
  value,
  placeholder,
  className,
  inputRef,
  ...props
}, ref) => {
  const displayValue = isFile(value) ? value.name : value;
  return React.createElement(React.Fragment, null, React.createElement(HiddenFileInput, _extends({
    ref: inputRef
  }, props)), React.createElement(FormControlWithAddon.Input, {
    ref: ref,
    as: "span",
    className: classNames(className, 'h-auto', !value && formStyles.placeholder)
  }, displayValue || placeholder && React.createElement(FormattedMessage, placeholder)));
});
FormControlFileInput.displayName = 'FormControlFileInput';

function UploadFileButton({
  theme,
  disabled,
  children
}) {
  return React.createElement(Button, {
    as: "span",
    type: null,
    theme: theme,
    disabled: disabled
  }, children || React.createElement(FormattedMessage, {
    id: "@bfly/ui:formControlFile.upload",
    defaultMessage: "Upload"
  }));
}
/**
 * A file input styled like a button, for use cases that don't want the fake
 * input here.
 */


const UploadFileInputButton = React.forwardRef(({
  theme,
  children,
  ...props
}, ref) => // eslint-disable-next-line jsx-a11y/label-has-associated-control
React.createElement("label", {
  className: "m-0"
}, React.createElement(HiddenFileInput, _extends({
  ref: ref
}, props)), React.createElement(UploadFileButton, {
  theme: theme,
  disabled: props.disabled
}, children)));
UploadFileInputButton.displayName = 'UploadFileInputButton';

function ClearFileButton({
  theme,
  onClick
}) {
  return React.createElement(Button, {
    theme: theme,
    onClick: onClick
  }, React.createElement(FormattedMessage, {
    id: "@bfly/ui:formControlFile.clear",
    defaultMessage: "Clear"
  }));
}

export { UploadFileInputButton, ClearFileButton };
const propTypes = {
  /** The visual style of the input */
  theme: PropTypes.oneOf(['dark', 'hero', 'light']).isRequired,

  /** Visually style the control as invalid */
  invalid: PropTypes.bool
};
/**
 * An HTML file input, with some custom styling.
 */

const FormControlFile = React.forwardRef(({
  invalid,
  theme,
  className,
  ...props
}, ref) => {
  const inputRef = useRef();
  return React.createElement(FormControlWithAddon, _extends({}, props, {
    ref: ref,
    inputRef: inputRef,
    as: "label",
    theme: theme,
    invalid: invalid,
    className: className,
    htmlFor: props.id,
    inputAs: FormControlFileInput,
    addon: !props.value ? React.createElement(UploadFileButton, {
      disabled: props.disabled
    }) : React.createElement(ClearFileButton, {
      onClick: e => {
        e.preventDefault();
        props.onChange(null);
        inputRef.current.focus();
      }
    })
  }));
});
FormControlFile.displayName = 'FormControlFile';
FormControlFile.propTypes = propTypes;
FormControlFile.Input = FormControlFileInput;
export default FormControlFile;